import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Upload } from "lucide-react";
import { formatImageUrl } from "@/lib/general";

interface ImageData {
  file: File;
  preview: string;
  url?: string;
}

interface ImageStepProps {
  images: ImageData[];
  uploadingImages: boolean;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  onNext?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  isDragging?: boolean;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
}

const ImageStep: React.FC<ImageStepProps> = ({
  images,
  uploadingImages,
  handleImageUpload,
  removeImage,
  onNext,
  onKeyDown,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
}) => {
  return (
    <div className="w-full" onKeyDown={onKeyDown} tabIndex={0}>
      {images.length > 0 ? (
        <div className="grid grid-cols-4 gap-2 xl:gap-4 px-2">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={formatImageUrl(
                  image.url || image.preview,
                  "medium",
                  false
                )}
                alt={`Preview ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg"
              />
              <button
                onClick={() => removeImage(index)}
                className="z-20 absolute -top-2 -right-2 bg-white rounded-full ring-1 ring-neutral-300 w-5 h-5 flex items-center justify-center shadow-sm hover:bg-neutral-50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <XMarkIcon className="w-3 h-3 text-neutral-500" />
              </button>
            </div>
          ))}
          {uploadingImages && (
            <div className="relative">
              <div className="w-full h-24 bg-neutral-100 rounded-lg flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </div>
          )}
          {images.length < 4 && !uploadingImages && (
            <div>
              <input
                type="file"
                accept="image/png, image/jpeg, image/webp"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                multiple
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer h-24 px-1 border-1 border-dashed border-neutral-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:bg-neutral-50 transition-colors"
              >
                <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center">
                  {uploadingImages ? (
                    <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Upload
                      className="w-4 h-4 text-neutral-500"
                      strokeWidth={1.5}
                    />
                  )}
                </div>
                <div>
                  <p className="text-neutral-600 text-xs font-light text-center">
                    {uploadingImages ? "Uploading..." : "Upload"}
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`border-1 border-dashed border-neutral-300 rounded-xl p-6 text-center ${
            isDragging ? "bg-indigo-50 border-indigo-300" : ""
          }`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <input
            type="file"
            accept="image/png, image/jpeg, image/webp"
            onChange={handleImageUpload}
            className="hidden"
            id="image-upload"
            multiple
            disabled={uploadingImages}
          />
          <label
            htmlFor="image-upload"
            className={`cursor-pointer flex flex-col items-center gap-3 ${
              uploadingImages ? "opacity-50" : ""
            }`}
          >
            <div className="w-14 h-14 bg-neutral-100 rounded-full flex items-center justify-center">
              {uploadingImages ? (
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Upload className="w-6 h-6 text-neutral-500" />
              )}
            </div>
            <div>
              <p className="text-neutral-600 text-sm">
                {uploadingImages
                  ? "Uploading..."
                  : isDragging
                  ? "Drop images here"
                  : "Click to upload or drag and drop"}
              </p>
              <p className="text-neutral-400 text-xs mt-1">
                PNG, JPG or WEBP (max 4 images)
              </p>
            </div>
          </label>
        </div>
      )}
    </div>
  );
};

export default ImageStep;
